import { post, getJson } from './request'

/**
 * 首页及详情页数据拉取。
 * 字段命名尽量贴近小程序原始返回，并在这里做归一化，
 * 让视图层使用统一字段（id / name / cover / ...）。
 */

// 轮播图：info/banner/list -> rows[]
export async function fetchBanners (company = '2') {
  const data = await post('zjs/mini/info/banner/list', { company, pageNum: 1, pageSize: 8 })
  return (data.rows || []).map(b => ({
    bannerId: b.bannerId,
    title: b.title,
    icon: b.icon,
    url: b.url,
    type: b.type
  }))
}

// 精选产品：product/list（data 字段即产品数据；兼容直接返回 rows 的情况）
export async function fetchProducts () {
  const data = await post('zjs/mini/product/list', { pageNum: 1, pageSize: 20 })
  // 优先取 data（可能为字符串/对象），兜底用 rows（与小程序 queryProductList 逻辑一致）
  let list = []
  const raw = data.data
  if (typeof raw === 'string') {
    try { list = JSON.parse(raw) } catch (e) { list = [] }
  } else if (Array.isArray(raw)) {
    list = raw
  } else if (raw && typeof raw === 'object') {
    list = raw.list || raw.records || raw.rows || raw.content || raw.data || raw.products || []
  }
  if (!list.length) list = data.rows || []
  return list.map(p => ({
    id: p.id,
    name: p.name,
    cover: p.cover,
    summary: p.summary,
    content: p.content,
    typeName: p.typeName,
    secondTypeName: p.secondTypeName,
    sourcePlace: p.sourcePlace
  }))
}

// 产品详情：product/detail，参数为 id，返回 data
export async function fetchProductDetail (id) {
  const data = await post('zjs/mini/product/detail', { id })
  const p = data.data || {}
  return {
    id: p.id,
    name: p.name,
    cover: p.cover,
    summary: p.summary,
    content: p.content,
    typeName: p.typeName,
    secondTypeName: p.secondTypeName,
    thirdTypeName: p.thirdTypeName,
    sourcePlace: p.sourcePlace
  }
}

// ============ 精选产品（按类型分类展示 / 分页） ============

// 一级产品类型：product/type/firstLevel（POST，支持 pageNum/pageSize 分页）
export async function fetchProductFirstTypes ({ pageNum = 1, pageSize = 12 } = {}) {
  const data = await post('zjs/mini/product/type/firstLevel', { pageNum, pageSize })
  return {
    rows: (data.rows || []).map(t => ({
      id: t.id,
      name: t.name,
      level: t.level,
      parentId: t.parentId,
      parentName: t.parentName,
      sort: t.sort
    })),
    total: data.total || 0
  }
}

// 产品类型树：product/type/tree（POST，参数为一级类型 id，data.children 为二级/三级）
export async function fetchProductTypeTree (firstLevelId) {
  const data = await post('zjs/mini/product/type/tree', { id: firstLevelId })
  const node = data.data || {}
  return {
    id: node.id,
    name: node.name,
    children: Array.isArray(node.children) ? node.children : []
  }
}

// 产品列表（按类型）：product/listByType（POST，typeId 为一级类型 id）
export async function fetchProductsByType ({ typeId, pageNum = 1, pageSize = 12 } = {}) {
  const data = await post('zjs/mini/product/listByType', { typeId, pageNum, pageSize })
  return {
    rows: (data.rows || []).map(p => ({
      id: p.id,
      name: p.name,
      cover: p.cover,
      summary: p.summary,
      content: p.content,
      typeName: p.typeName,
      secondTypeId: p.secondTypeId,
      secondTypeName: p.secondTypeName,
      thirdTypeName: p.thirdTypeName,
      sourcePlace: p.sourcePlace
    })),
    total: data.total || 0
  }
}

// 行业专家：info/expert/list -> rows[]（主键 expertId）
export async function fetchExperts () {
  const data = await post('zjs/mini/info/expert/list', { pageNum: 1, pageSize: 20 })
  return (data.rows || []).map(e => ({
    id: e.expertId,
    name: e.expertName,
    cover: e.expertCover,
    tags: e.expertTags,
    profile: e.expertProfile,
    introduction: e.expertIntroduction
  }))
}

// 政经动态：info/dynamic/list -> rows[]（主键 trendId）
export async function fetchDynamics (company = '2') {
  const data = await post('zjs/mini/info/dynamic/list', { company, pageNum: 1, pageSize: 20 })
  return (data.rows || []).map(d => ({
    id: d.trendId,
    name: d.dynamicName,
    cover: d.dynamicCover,
    introduction: d.dynamicIntroduction,
    link: d.dynamicLink
  }))
}

// 出海案例：anli.json（GET 静态配置） -> data[]
export async function fetchCases () {
  const data = await getJson('anli.json')
  return (data.data || []).map(c => ({
    id: c.id,
    url: c.url,
    tagText: c.tagText,
    title: c.title,
    content: c.content,
    headerBg: c.headerBg,
    titleColor: c.titleColor,
    descColor: c.descColor,
    bgContent: c.bgContent,
    svcIntro: c.svcIntro,
    services: c.services || [],
    results: c.results || []
  }))
}

// ============ 列表页 / 分页 / 详情（对齐小程序 expert_list / expert / dynamic_list） ============

// 专家分类：info/sys_data/get（dictType=sys_expert_type）-> tabs[]
export async function fetchExpertTypes () {
  const data = await post('zjs/mini/info/sys_data/get', { dictType: 'sys_expert_type' })
  const arr = (data.data || []).map(d => ({ code: d.dictValue, name: d.dictLabel }))
  return [{ code: '', name: '全部' }, ...arr]
}

// 行业专家（分页 + 分类过滤）：info/expert/list -> { rows, total }
export async function fetchExpertsPage ({ pageNum = 1, pageSize = 9, expertType = '' } = {}) {
  const data = await post('zjs/mini/info/expert/list', { pageNum, pageSize, expertType })
  return {
    rows: (data.rows || []).map(e => ({
      id: e.expertId,
      name: e.expertName,
      cover: e.expertCover,
      tags: e.expertTags,
      profile: e.expertProfile,
      type: e.expertType
    })),
    total: data.total || 0
  }
}

// 专家详情：country/expert/info（expertId） -> data
export async function fetchExpertDetail (id) {
  const data = await post('zjs/mini/country/expert/info', { expertId: id })
  const e = data.data || {}
  return {
    id: e.expertId,
    name: e.expertName,
    cover: e.expertCover,
    tags: e.expertTags,
    profile: e.expertProfile,
    introduction: e.expertIntroduction,
    contact: e.expertContact
  }
}

// 政经动态（分页）：info/dynamic/list（company） -> { rows, total }
export async function fetchDynamicsPage ({ pageNum = 1, pageSize = 10 } = {}) {
  const data = await post('zjs/mini/info/dynamic/list', { company: '2', pageNum, pageSize })
  return {
    rows: (data.rows || []).map(d => ({
      id: d.trendId,
      name: d.dynamicName,
      cover: d.dynamicCover,
      introduction: d.dynamicIntroduction,
      link: d.dynamicLink
    })),
    total: data.total || 0
  }
}

// 投资资讯（首页区块）：info/investment/list（company） -> rows[]
export async function fetchInvestment (company = '2') {
  const data = await post('zjs/mini/info/investment/list', { company, pageNum: 1, pageSize: 20 })
  return (data.rows || []).map(d => ({
    id: d.trendId,
    name: d.dynamicName,
    cover: d.dynamicCover,
    introduction: d.dynamicIntroduction,
    link: d.dynamicLink
  }))
}

// 投资资讯（分页列表）：info/investment/list（company） -> { rows, total }
export async function fetchInvestmentPage ({ pageNum = 1, pageSize = 10 } = {}) {
  const data = await post('zjs/mini/info/investment/list', { company: '2', pageNum, pageSize })
  return {
    rows: (data.rows || []).map(d => ({
      id: d.trendId,
      name: d.dynamicName,
      cover: d.dynamicCover,
      introduction: d.dynamicIntroduction,
      link: d.dynamicLink
    })),
    total: data.total || 0
  }
}

// 商务活动（首页区块）：info/business/list（company, status） -> rows[]
export async function fetchBusiness (company = '2', status = '0') {
  const data = await post('zjs/mini/info/business/list', { company, status, pageNum: 1, pageSize: 20 })
  return (data.rows || []).map(d => ({
    id: d.businessActivityId,
    title: d.title,
    cover: d.icon,
    startDate: d.startDate,
    endDate: d.endDate,
    status: d.status,
    url: d.url
  }))
}

// 商务活动（分页列表，按状态区分）：info/business/list（company, status） -> { rows, total }
export async function fetchBusinessPage ({ status = '0', pageNum = 1, pageSize = 10 } = {}) {
  const data = await post('zjs/mini/info/business/list', { company: '2', status, pageNum, pageSize })
  return {
    rows: (data.rows || []).map(d => ({
      id: d.businessActivityId,
      title: d.title,
      cover: d.icon,
      startDate: d.startDate,
      endDate: d.endDate,
      status: d.status,
      url: d.url
    })),
    total: data.total || 0
  }
}

// ============ 国家资讯（country） ============

// 国家列表（按大洲分组）：country/list -> [{ continent, countries:[{id,name,icon}] }]
export async function fetchCountryList () {
  const data = await post('zjs/mini/country/list', { name: '' })
  return (data.rows || []).map(g => ({
    continent: g.continents,
    countries: (g.dataList || []).map(c => ({
      id: c.countryId,
      name: c.countryName,
      icon: c.icon,
      status: c.status
    }))
  }))
}

// 国家详情 - 官方必读资讯：country/link/info -> rows[]
export async function fetchCountryLinkInfo (countryId) {
  const data = await post('zjs/mini/country/link/info', { countryId })
  return (data.rows || []).map(r => ({
    id: r.linkId,
    name: r.linkName,
    cover: r.linkCover,
    type: r.linkType,
    url: r.linkUrl,
    institution: r.institution,
    content: r.content
  }))
}

// 国家详情 - 国家（地区）资讯：country/resource/list -> rows[]
export async function fetchCountryResource (countryId) {
  const data = await post('zjs/mini/country/resource/list', { countryId, pageNum: 1, pageSize: 20 })
  return (data.rows || []).map(r => ({
    id: r.id,
    title: r.title,
    icon: r.icon,
    type: r.type,
    url: r.url
  }))
}

// 国家详情 - 视频资料：country/vedio/list -> rows[]
export async function fetchCountryVideo (countryId) {
  const data = await post('zjs/mini/country/vedio/list', { countryId, pageNum: 1, pageSize: 20 })
  return (data.rows || []).map(r => ({
    id: r.id,
    title: r.title,
    icon: r.icon,
    type: r.type,
    url: r.url
  }))
}

// 国家详情 - 当地专家/机构：country/expert/list -> { rows, total }
export async function fetchCountryExperts (countryId, { pageNum = 1, pageSize = 20 } = {}) {
  const data = await post('zjs/mini/country/expert/list', { countryId, pageNum, pageSize })
  return {
    rows: (data.rows || []).map(e => ({
      id: e.expertId,
      name: e.expertName,
      countryName: e.countryName,
      tags: e.expertTags,
      cover: e.expertCover,
      profile: e.expertProfile
    })),
    total: data.total || 0
  }
}

// 国别报告：country/report/info -> { content(完整文档URL), about(概述富文本) }；无报告时 data 为 null
export async function fetchCountryReport (countryId) {
  const data = await post('zjs/mini/country/report/info', { countryId })
  const d = data.data || null
  return {
    content: d ? (d.content || '') : '',
    about: d ? (d.about || '') : ''
  }
}

// 出海服务：chuhai_service.json -> 服务分类列表（已过滤未展示项）
export async function fetchOverseaService () {
  const data = await getJson('chuhai_service.json')
  const list = data && data.data ? data.data : []
  return list.filter(item => item.isShow !== false)
}

// 出海研究报告（分页）：info/overseas_report/list -> { rows, total }
export async function fetchOverseaReportPage ({ pageNum = 1, pageSize = 10 } = {}) {
  const data = await post('zjs/mini/info/overseas_report/list', { pageNum, pageSize })
  return {
    rows: (data.rows || []).map(r => ({
      id: r.id,
      tag: r.tag,
      title: r.title,
      summary: r.summary,
      createTime: r.createTime,
      url: r.url
    })),
    total: data.total || 0
  }
}

// ============ 功能入口跳转 ============

// 出海测算外链（按需求直接跳转）
export const OVERSEAS_CESUAN_URL = 'https://www.roadlinkglobal.com/#/destination'

const AI_FINANCE_URL = 'https://clawtax.com.cn/partner/zhengjingshi/chat'

// 生成稳定的浏览器唯一标识，作为 externalUserId 传给服务端（小程序里是 openid）
function getWebUid () {
  try {
    let uid = localStorage.getItem('glb_web_uid')
    if (!uid) {
      uid = 'web_' + Math.random().toString(36).slice(2) + Date.now().toString(36)
      localStorage.setItem('glb_web_uid', uid)
    }
    return uid
  } catch (e) {
    return 'web_' + Date.now().toString(36)
  }
}

/**
 * 财税 AI 跳转：与小程序 ai_finance 组件完全一致——
 * 先调用 ai/getAiFinanceToken 获取 token（服务端返回在 msg 字段），
 * 再拼接 clawtax 对话地址（mode=embedded&lang=zh-CN&token=xxx）并打开。
 */
export async function openAiFinance () {
  let token = ''
  try {
    const res = await post('zjs/mini/ai/getAiFinanceToken', { id: getWebUid(), name: '政经事用户' })
    token = (res && res.msg) || ''
  } catch (e) {
    token = ''
  }
  const parts = ['mode=embedded&lang=zh-CN']
  if (token) parts.push('token=' + token)
  const url = AI_FINANCE_URL + '?' + parts.join('&')
  window.open(url, '_blank')
}
